import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants, fetchAdminMeta, updateTenant, verifyTenantSso } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

// Presentation only — the valid auth_mode values come from /admin/meta.
// ADR-0043: the axis is WHO OWNS THE USER STORE. Hosted = Waive-operated passwordless (SuperTokens,
// ADR-0050) — no per-tenant realm/issuer; the superseded ADR-0049 per-tenant Keycloak realm is gone.
const AUTH_MODE_LABELS = {
    federated: "Federated — the tenant's own IdP is a trusted issuer",
    hosted: 'Hosted — Waive-operated passwordless (SuperTokens: email OTP + passkeys)',
};

function Identity({ tenantCode }) {
    const { can } = useAuth();
    const [tenant, setTenant] = useState(null);
    const [authModes, setAuthModes] = useState([]);
    const [form, setForm] = useState({ auth_mode: 'federated', issuer: '', jwks_uri: '', audience: '', roles_claim: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setNotice(null);
        setVerifyResult(null);
        try {
            const [tenantsRes, meta] = await Promise.all([fetchTenants(), fetchAdminMeta()]);
            setAuthModes(meta.auth_modes || []);
            const t = (tenantsRes.tenants || []).find(x => x.code === tenantCode);
            if (!t) { setError(`Tenant "${tenantCode}" not found.`); setTenant(null); return; }
            setTenant(t);
            setForm({
                auth_mode: t.auth_mode || 'federated',
                issuer: t.issuer || '',
                jwks_uri: t.jwks_uri || '',
                audience: t.audience || '',
                roles_claim: t.roles_claim || '',
            });
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to load tenant identity.');
        } finally {
            setLoading(false);
        }
    }, [tenantCode]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        setError(null);
        setNotice(null);
        // ADR-0031 federated-IdP guardrail: the tenant's IdP needs BOTH issuer and JWKS, or neither (a tenant on
        // the shared/dev issuer). Saving just one leaves the tenant half-provisioned (the API 400s and
        // the DB CHECK rejects it anyway) — catch it here for a clearer message.
        if (form.auth_mode !== 'hosted') {
            const hasIssuer = !!form.issuer.trim();
            const hasJwks = !!form.jwks_uri.trim();
            if (hasIssuer !== hasJwks) {
                setError('Issuer and JWKS URI must be set together — provide both, or clear both.');
                return;
            }
        }
        setSaving(true);
        try {
            const payload = form.auth_mode === 'hosted'
                // Hosted (ADR-0050): Waive-operated SuperTokens passwordless. One shared platform issuer
                // (trusted in app-api's OIDC_ISSUERS); the tenant is resolved from the session's tenant_id
                // CLAIM, not iss→tenant — so there's no per-tenant issuer/JWKS to store.
                ? {
                    auth_mode: 'hosted',
                    issuer: null,
                    jwks_uri: null,
                    audience: null,
                    roles_claim: null,
                }
                // Federated: the customer supplies their own IdP details.
                : {
                    auth_mode: 'federated',
                    issuer: form.issuer.trim() || null,
                    jwks_uri: form.jwks_uri.trim() || null,
                    audience: form.audience.trim() || null,
                    roles_claim: form.roles_claim.trim() || null,
                };
            await updateTenant(tenant.id, payload);
            setNotice('Identity configuration saved.');
            await load();
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    // Preflight the SAVED federated-IdP config (the endpoint reads the DB, not the unsaved form) — a bad
    // issuer/JWKS is caught here rather than at a member's first login (tech-debt §C).
    const handleVerify = async () => {
        setError(null);
        setVerifyResult(null);
        setVerifying(true);
        try {
            setVerifyResult(await verifyTenantSso(tenant.code));
        } catch (e) {
            setError(e.response?.data?.detail || 'Verification failed.');
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return <div className="page-content"><Spinner animation="border" size="sm" /> Loading…</div>;
    }

    const isHosted = form.auth_mode === 'hosted';
    // Hosted (SuperTokens) needs no per-tenant IdP config, so it's "configured" by mode alone.
    const configured = tenant?.auth_mode === 'hosted' || !!tenant?.issuer;
    // Verify applies to a SAVED federated tenant with both issuer + JWKS (the endpoint reads the stored config).
    const canVerify = tenant?.auth_mode === 'federated' && !!tenant?.issuer && !!tenant?.jwks_uri;
    const mark = v => (v === true ? '✓' : v === false ? '✗' : '—');

    return (
        <div className="page-content">
            <div className="page-header d-flex align-items-center gap-2">
                <h1 className="page-title mb-0">Identity</h1>
                {configured
                    ? <Badge bg="success" style={{ fontSize: 11 }}>Configured</Badge>
                    : <Badge bg="secondary" style={{ fontSize: 11 }}>Not configured</Badge>}
            </div>

            <p className="text-muted" style={{ fontSize: 13, maxWidth: 720 }}>
                How this tenant's members authenticate. All changes are audited.
            </p>

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
            {notice && <Alert variant="success" className="py-2" style={{ fontSize: 13 }}>{notice}</Alert>}

            {tenant && (
                <Card className="border-0 shadow-sm">
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-3" style={{ maxWidth: 480 }}>
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Auth mode</Form.Label>
                                <Form.Select size="sm" data-testid="identity-auth-mode" value={form.auth_mode} onChange={e => set('auth_mode', e.target.value)}>
                                    {(authModes || []).map(m => (
                                        <option key={m} value={m}>{AUTH_MODE_LABELS[m] || m}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <hr className="my-3" />

                            {!isHosted ? (
                                // ─── Federated: the customer's own IdP ──────────────────────────────
                                <>
                                    <div className="mb-3 text-muted" style={{ fontSize: 12 }}>
                                        Enter the tenant's own IdP (Entra ID, Okta, their own realm, …).
                                    </div>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Issuer</Form.Label>
                                        <Form.Control size="sm" value={form.issuer}
                                            onChange={e => set('issuer', e.target.value)}
                                            placeholder="https://idp.acme.com/…" />
                                        <Form.Text className="text-muted">The token <code>iss</code> claim — unique across tenants.</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>JWKS URI</Form.Label>
                                        <Form.Control size="sm" value={form.jwks_uri}
                                            onChange={e => set('jwks_uri', e.target.value)}
                                            placeholder="https://idp.acme.com/…/certs" />
                                    </Form.Group>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Audience</Form.Label>
                                                <Form.Control size="sm" value={form.audience}
                                                    onChange={e => set('audience', e.target.value)}
                                                    placeholder="waive-services" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Roles claim</Form.Label>
                                                <Form.Control size="sm" value={form.roles_claim}
                                                    onChange={e => set('roles_claim', e.target.value)}
                                                    placeholder="realm_access.roles" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                // ─── Hosted: Waive-operated SuperTokens passwordless (ADR-0050) ───
                                <>
                                    <div className="mb-2" style={{ fontSize: 13 }}>
                                        Waive operates this tenant's login — <strong>passwordless</strong>: an email
                                        one-time code plus passkeys (SuperTokens). There is <strong>no customer IdP and
                                        no per-tenant realm or issuer to configure</strong>; the platform uses one shared
                                        issuer and scopes each member to this tenant by the session's tenant claim.
                                    </div>
                                    <ul className="text-muted mb-0" style={{ fontSize: 12 }}>
                                        <li>Members <strong>self-provision on first sign-in</strong> (email OTP) — upload
                                            the roster on the <strong>Members</strong> tab; there is no realm to provision.</li>
                                        <li>Set their white-label login URL on the <strong>Domain</strong> tab.</li>
                                        <li>To remove a member's access, <strong>offboard</strong> them on the{' '}
                                            <strong>Members</strong> tab.</li>
                                    </ul>
                                </>
                            )}

                            <div className="mt-3 d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={handleSave}
                                    disabled={saving || !can.onboardTenant()}
                        title={can.onboardTenant() ? undefined : "Requires the waive-onboarding role"}>
                                    {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FontAwesomeIcon icon="key" className="me-1" />}
                                    Save
                                </Button>
                                {canVerify && (
                                    <Button variant="outline-secondary" size="sm" onClick={handleVerify} disabled={verifying || !can.onboardTenant()}
                                        title={can.onboardTenant()
                                            ? "Check the saved issuer + JWKS are reachable and consistent"
                                            : "Requires the waive-onboarding role"}>
                                        {verifying && <Spinner size="sm" animation="border" className="me-1" />}
                                        Verify SSO
                                    </Button>
                                )}
                            </div>

                            {verifyResult && (
                                <Alert variant={verifyResult.ok ? 'success' : 'warning'} className="py-2 mt-3 mb-0"
                                    style={{ fontSize: 12, maxWidth: 640 }}>
                                    <div className="fw-semibold mb-1">
                                        {verifyResult.ok
                                            ? 'SSO configuration verified — reachable and consistent.'
                                            : 'SSO configuration has issues.'}
                                    </div>
                                    <ul className="mb-1 ps-3">
                                        <li>OIDC discovery reachable: {mark(verifyResult.discovery_reachable)}</li>
                                        <li>Issuer matches discovery: {mark(verifyResult.issuer_matches)}</li>
                                        <li>JWKS reachable: {mark(verifyResult.jwks_reachable)}
                                            {verifyResult.jwks_reachable ? ` (${verifyResult.jwks_key_count} key${verifyResult.jwks_key_count === 1 ? '' : 's'})` : ''}</li>
                                    </ul>
                                    {(verifyResult.errors || []).map((e, i) => <div key={`e${i}`} className="text-danger">✗ {e}</div>)}
                                    {(verifyResult.warnings || []).map((w, i) => <div key={`w${i}`} className="text-warning">⚠ {w}</div>)}
                                </Alert>
                            )}
                        </Form>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default Identity;
